/* eslint-disable @typescript-eslint/member-ordering */
import { AfterContentChecked, AfterViewChecked, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModuleManager } from 'src/shared/models/module-manager';
import { QmEvaluationsCoachingService } from './qm-evaluations-coaching.service';
import { ModuleThemesService } from 'src/app/reusables/services/module-themes.service';
import { DatePipe } from '@angular/common';
import { AgentData, AgentDropdown, AvgScoreInfo, CoachingObj, CoachingPoint, EvaluationsCoachingData, NearestPointInfo, PointObject, ScoresObj } from './qm-evaluations-coaching.model';
import * as Highcharts from 'highcharts';
import { ModuleManagerExportFormat, ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { GroupSelectorDTO } from 'src/AngularJS/Reusables/group-selector.models';
import { GroupSelectorService } from 'src/app/reusables/group-selector/group-selector.service';
import { BaseGroupType } from 'src/AngularJS/Models/group-type.model';
import { GroupSelectorComponent } from 'src/app/reusables/group-selector/group-selector.component';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { CxoneTeamsComponent } from '../../toolbar-items/cxone-teams/cxone-teams.component';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { Group } from 'src/AngularJS/Models/group.model';

@Component({
    selector: 'cv-qm-evaluations-coaching',
    templateUrl: './qm-evaluations-coaching.component.html',
    styleUrls: ['./qm-evaluations-coaching.component.scss']
})
export class QmEvaluationsCoachingComponent implements OnInit, AfterContentChecked, AfterViewChecked {

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() settingsManager: SlideoutSettingsManager;
    @ViewChild("groupSelectorDTOComp") groupSelectorDTOComp: GroupSelectorComponent;

    isAborting = false;
    abortLoad: () => void = null;

    // from template
    public data: EvaluationsCoachingData[] = null; // A copy of data returned by loadData will be stored here.
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;

    tooltipContext = null;
    public agents: AgentDropdown[] = [];
    public selectedAgents: AgentDropdown[] = [];
    public selectedAgentsDTO: GroupSelectorDTO = null;
    public empGroup1List: Map<number, Group>;

    public myChips: AgentDropdown[] = [];
    public agentsData: AgentData[] = [];

    public loading = false;
    public highcharts = Highcharts;
    public updateChart = false;
    public dataReady = false;
    public noData = false;
    public noAgentsSelected = true;

    public config: Highcharts.Options = { // default is empty chart
        title: {
            text: ""
        },
        chart: {
            zoomType: "xy",
        },
        plotOptions: {
            series: {
                connectNulls: true,
                // pointPlacement: 'on', // remove padding on x axis
                marker: {
                    radius: 9,
                    symbol: 'circle'
                },
                dataLabels: {
                    enabled: true,
                    y: 14,
                    color: '#FFFFFF',
                },
                states: {
                    inactive: {
                        opacity: .8 // change if we dont want all labels to disappear when tooltip is shown
                    }
                },
                stickyTracking: false,
                // findNearestPointBy: 'x', // defaults to x looks like you cant disable this (causes the line on hover to show tooltip of nearest point)
            },
        },
        yAxis: {
            id: "yAxis",
            title: {
                text: "Score"
            },
            labels: {
                // hiding the first an last labels since we want 0 - 100 made the range larger so the coaching flags or points were farther from the edges of the chart
                // if they are too close to the edge the datalabels and flags wouldnt show up
                formatter() {
                    if (this.isFirst || this.isLast) {
                        return "";
                    }
                    else {
                        return this.value.toString();
                    }
                },
            },
            min: -20, // dynamically reset after data is crunched in setChartYAxisMinMax()
            max: 120,
        },
        xAxis: {
            categories: [], // set in create chart
            minRange: 0.5,
        },
        legend: {
            enabled: false,
        },
        tooltip: { // formatter set in create chart
            useHTML: true,
            split: false,
            backgroundColor: "#FFFFFF",
            stickOnContact: true,
            snap: 0, // point hit radius to show tooltip - FIX LATER - line hover also causes tooltip to appear (of nearest point)
            // outside: true, // should work so tooltip doesnt get cutoff in small chart, figure out why our code causes this to go behind widget
        },
        series: [ // set in showChips()
        ],
        credits: {
            enabled: false
        },
    };

    // from template
    private localizationStrings: Map<string, string> = new Map<string, string>();
    private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.

    private tagDeleted = false;
    private chartObject = null;
    private currentTheme = null;
    private colorIndex = 0;
    private stateExists = false;

    private dates = [];
    private fullDates = []; // initiallized after all data is crunched
    private chartLabels = []; // initiallized after all data is crunched
    private tempSelectedAgents: number[] = [0]; //keeps track of selected agents before publish
    // used to color flags
    private colorMap: {[id: string]: string} =
        {"rgba(155,207,181,1)": 'invert(83%) sepia(18%) saturate(442%) hue-rotate(97deg) brightness(93%) contrast(86%)',
            "rgba(249,106,104,1)": 'invert(41%) sepia(39%) saturate(2749%) hue-rotate(332deg) brightness(134%) contrast(95%)',
            "rgba(181,230,229,1)": 'invert(87%) sepia(11%) saturate(589%) hue-rotate(130deg) brightness(105%) contrast(83%)',
            "rgba(253,203,98,1)": 'invert(85%) sepia(27%) saturate(985%) hue-rotate(337deg) brightness(101%) contrast(98%)',
            "rgba(254,237,176,1)": 'invert(81%) sepia(94%) saturate(197%) hue-rotate(325deg) brightness(104%) contrast(99%)',
            "rgba(108,141,151,1)": 'invert(57%) sepia(12%) saturate(724%) hue-rotate(147deg) brightness(91%) contrast(87%)',
            "rgba(184,193,165,1)": 'invert(79%) sepia(23%) saturate(201%) hue-rotate(38deg) brightness(91%) contrast(93%)',
            "rgba(248,164,121,1)": 'invert(69%) sepia(24%) saturate(815%) hue-rotate(333deg) brightness(99%) contrast(96%)',
            "rgba(89,187,214,1)": 'invert(73%) sepia(25%) saturate(879%) hue-rotate(149deg) brightness(90%) contrast(85%)',
            "rgba(213,204,144,1)": 'invert(83%) sepia(23%) saturate(470%) hue-rotate(15deg) brightness(95%) contrast(89%)',
        };

    constructor(
        private service: QmEvaluationsCoachingService,
        private themesService: ModuleThemesService,
        private datePipe: DatePipe,
        private groupSelectorService: GroupSelectorService,
        private configuration: CvConfigurationService,
        private navigationService: NavigationService,
    ) { }

    // removes opacity from the colors brought back from the themes service
    removeOpacity() {
        let counter = 0;
        this.currentTheme.forEach(colorString => {
            this.currentTheme[counter] = colorString.replace('0.7', '1');
            counter++;
        });
    }

    async ngOnInit() {
        // turning off settings emp selector as there is a custom one in the settings portion, done this way for state reasons
        this.moduleManager.getModuleInfo().DTO.SettingEmpSelector = false;

        Highcharts.AST.allowedAttributes.push('onclick');

        // Ensure a theme exists
        if (this.currentTheme == null) {
            this.currentTheme = this.themesService.GetThemeByIndex(3);
            this.removeOpacity();
        }

        this.moduleManager.showLoader = true;
        await this.moduleManager.forceLoadSettings();

        // needed to ensure that the selector is correctly identified as a multiselect dropdown
        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: async settings => {
                if (this.tempSelectedAgents.length > 0)
                    settings.empSelectorDTO._SelectedGroup1IDList = this.tempSelectedAgents;
                const allGroups = (await this.groupSelectorService.loadFilteredGroups(this.moduleKey, BaseGroupType.Employee, 1)).data;
                this.empGroup1List = new Map(allGroups[1].Groups.map(g => [g.GroupID, g]));
                settings.empSelectorDTO._SelectedGroup1IDList = settings.empSelectorDTO._SelectedGroup1IDList
                    .filter(id => this.empGroup1List.has(id));
                this.agentGroupsChanged(settings.empSelectorDTO);
                if (this.isFirstLoad) {
                    await this.loadData(false); // needed for initial load in - can look into - also called in agentGroupsChanged() above. so technically shouldnt need
                }

            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: async isAutoRefresh => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                await this.loadData(isAutoRefresh);
                this.showChipsAddToChart();
                requestAnimationFrame( async () => {
                    this.groupSelectorDTOComp.selectedGroups[1] = this.selectedAgents.map(agent => agent.dtoID)
                        .filter(dtoID => dtoID != null);
                    await this.groupSelectorDTOComp?.saveSelectedGroups(2);
                });
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
            onExport: this.onExport.bind(this),
        });

        // Module Configuration:
        this.moduleManager.addState({
            name: "agentState",
            autoLoad: true,
            getStateData: () => this.selectedAgents,
            onStateLoaded: stateData => {
                if (!stateData) {
                    return;
                }
                this.stateExists = true;
                this.selectedAgents = stateData;
            }
        });

        this.moduleManager.addState({
            name: "agentGroupSelector",
            autoLoad: true,
            getStateData: () => this.tempSelectedAgents,
            onStateLoaded: stateData => {
                this.tempSelectedAgents = [];
            }
        });

        this.moduleManager.registerEvents({
            onResize: () => this.chartObject?.reflow(),
            onResizeStop: () => window.setTimeout(() => this.chartObject?.reflow(), 500),
        });

        this.moduleManager.addToolbarItem(CxoneTeamsComponent);
    }

    // called whenever agent dropdown is changed AKA the groupSelector
    public async agentGroupsChanged(e: GroupSelectorDTO) {
        this.tempSelectedAgents = e._SelectedGroup1IDList;
        this.noAgentsSelected = e._SelectedGroup1IDList?.length === 0 || e._SelectedGroup1IDList[0] === 0; //determines if we should show the no agents selected error
        // this function gets called when a chip is removed due to saveSelectedGroups function call
        // if a tag was deleted there is no reason for another data load since we can just remove the data from the chart
        if (this.isFirstLoad || this.tagDeleted) {
            return;
        }

        await this.loadData(false);

        if (this.data?.length === 0) {
            this.noData = true;
        }

        this.updateGroupSelectors(e);
        const allSelected = this.getSelectedAgents(e);
        this.setAgentsWithData(allSelected);
        this.setAgentsNoData(allSelected);

        this.moduleManager.saveState("agentState"); // important to save state before you update the agent names!
        this.updateNameForNoData();
        this.showChipsAddToChart();

        this.moduleManager.requestPublishNeeded();
    }

    // runs from front end after the end of createChart() via boolean flag dataReady
    public async initialGroupsLoaded(e: GroupSelectorDTO) {
        if (!this.isFirstLoad) {
            return;
        }
        if (this.data.length === 0) {
            this.isFirstLoad = false;
            this.noData = true;
            return;
        }
        this.updateGroupSelectors(e);
        if (this.stateExists) {
            this.initWithState();
            return;
        }

        const allSelected = this.getSelectedAgents(e);
        this.setAgentsWithData(allSelected);
        this.setAgentsNoData(allSelected);
        this.updateNameForNoData();

        this.showChipsAddToChart();
        this.isFirstLoad = false;
    }

    /** updates the groupSelector from the selectedAgents array that has been saved from state.
     * Then updates the settings side agent dropdown and initializes the chart. Used in the following function:
     *
     * @function intialGroupsLoaded()
     */
    async initWithState() {
        this.groupSelectorDTOComp.selectedGroups[1] = this.selectedAgents.map(agent => agent.dtoID).filter(dtoID => dtoID != null);
        // even though we are not using any agents from group 2 this assures that all agents from group 1 are saved before the dropdown is usable again
        await this.groupSelectorDTOComp?.saveSelectedGroups(2);

        this.updateNameForNoData();
        this.showChipsAddToChart();
        this.isFirstLoad = false;
    }

    /** updates the groupSelector in the component and the settings. Used in the following functions:
     *
     * @function intialGroupsLoaded()
     * @function agentGroupsChanged()
     */
    private updateGroupSelectors(e: GroupSelectorDTO) {
        this.selectedAgentsDTO = e;
    }
    /** Only grab those agents whos IDs exist in e._SelectedGroup1IDList.
     * Used in the following functions:
     *
     * @function intialGroupsLoaded()
     * @function agentGroupsChanged()
     */
    private getSelectedAgents(e) {
        let allSelected = [];

        allSelected = e._SelectedGroup1IDList.map(id => this.empGroup1List.get(id));

        allSelected = allSelected.filter(selected => selected != null);
        return allSelected;
    }


    /** finds those selected agents with data by comparing against the agents array which is filled in the dataLoad() function.
     * then creates the agent objects and adds them to the selected agents array. Used in the following functions:
     *
     * @function intialGroupsLoaded()
     * @function agentGroupsChanged()
     */
    private setAgentsWithData(allSelected: Group[]) {
        this.selectedAgents = this.agents.map(agent => {
            const group = allSelected.find(g => g.GroupName === agent.name);
            if (!group) {
                return null;
            }
            const name = agent.name;
            const id = agent.id;
            const color = agent.color;
            // FIX: use hasData flag to display noData sign on chip when available
            return {name, id, selected: true, color, hasData: true, dtoID: group.GroupID} as AgentDropdown;
        }).filter(agent => agent != null);
        this.agents = [...this.selectedAgents];
    }

    /** finds those selected agents with no data by comparing against the selectedAgents array which at this point
     * only has agents with data in it. then creates the agent objects and adds them to the selected agents array. Used in the following functions:
     *
     * @function intialGroupsLoaded()
     * @function agentGroupsChanged()
     */
    private setAgentsNoData(allSelected: Group[]) {
        const noData = allSelected.filter(g => !this.selectedAgents.find(agent => g.GroupName === agent.name));
        let counter = -1;
        const noDataSelected = noData.map(g => ({name: g.GroupName,
            id: counter--,
            selected: true,
            color: "",
            hasData: false,
            dtoID: g.GroupID} as AgentDropdown
        ));
        noDataSelected.forEach(noDataAgent => {
            this.selectedAgents.push(noDataAgent);
        });
    }

    /** used in below functions - this adds the " - No Data" suffix to the agent names for those agents without data which will be shown in the chips
     *
     * @function intialGroupsLoaded()
     * @function agentGroupsChanged()
     * @function initWithState()
     */
    private updateNameForNoData() {
        this.selectedAgents.forEach(agent => {
            if (!agent.hasData && !agent.name.includes("No Data")) {
                agent.name += " - No Data";
            }
        });
    }

    // this could be fixed to be more performant, had to use this hook becuase their component is removing the class that i add
    // this is called frequently at fixes it after that point, (and called many times after unecessarily) if they fix it call this
    // below where it is comented out in showChips()
    ngAfterContentChecked(): void {
        this.styleChipsNoData();
    }

    styleChipsNoData() {
        const list = document.querySelectorAll('.cxone-tag');
        list.forEach((chip) => {
            if(chip.innerHTML.includes('No Data')) {
                chip.classList.add("noDataEvalCoachingChip");
            }
        });

    }

    /**
     * Get formated data ready to export
     *
     * @param format
     */
    public async onExport(format: ModuleManagerExportFormat, isCXOne: boolean) {
        const response = await this.service.getDataForExport(this.moduleKey, format, isCXOne);
        return response;
    }

    // called by angular highcharts wrapper in the front end
    setChartInstance(event) {
        this.chartObject = event;
    }

    getIdx(name) {
        return this.agentsData.findIndex(i => i.name === name);
    }

    // used to update dropdown when a tag is deleted
    async onTagDeleted(event) {
        this.tagDeleted = true; // short circuits a data load

        this.removeSelectedTag(event);
        this.moduleManager.saveState("agentState");

        this.removeFromChart(event);
        this.updateDropdowns();

        this.moduleManager.requestPublishNeeded();
    }

    private removeSelectedTag(event: AgentDropdown) {
        this.selectedAgents.forEach(agent => {
            if (event.id === agent.id) {
                agent.selected = false;
            }
        });
        this.selectedAgents = this.selectedAgents.filter(i => i.selected);
    }

    private removeFromChart(event: AgentDropdown) {
        // only remove them if they exist in agentData else they arent on the chart
        if (this.agentsData.find(agent => agent.name === event.name)) {
            this.chartObject.get(event.name)?.remove();
            this.chartObject.get(event.name + "-coachings")?.remove();
        }
    }

    private async updateDropdowns(){
        // removing the deselected agent from the dropdown
        this.groupSelectorDTOComp.selectedGroups[1] = this.selectedAgents.map(agent => agent.dtoID).filter(dtoID => dtoID != null);

        // check if all selected to keep widget and settings in sync and functioning properly
        this.updateGroupHasAll();

        try {
            // even though we are not using any agents from group 2 this assures that all agents from group 1 are saved before the dropdown is usable again
            await this.groupSelectorDTOComp?.saveSelectedGroups(2);
        }
        catch (e) {
        }
        finally {
            this.tagDeleted = false; // lets data loads happen again after a tag is done being deleted
        }
    }

    updateGroupHasAll() {
        const hasCurrent = !this.configuration.isCxOne;
        if (this.groupSelectorDTOComp.selectedGroups[1].length !==
        this.groupSelectorDTOComp.groupsByLevel[1].Groups.length - (hasCurrent ? 1 : 0)) { // subtract 1 because of Current being added to the list in regular dashboard only
            this.groupSelectorDTOComp.hasAll[1] = false;
        } else {
            this.groupSelectorDTOComp.hasAll[1] = true;
        }
    }

    /**
     * used to populate highchart with data that has been filled into the selectedAgents array.
     * showChips is used in the following functions:
     *
     * @function intialGroupsLoaded()
     * @function agentGroupsChanged()
     * @function initWithState()
     * @function onRefresh()
     */
    showChipsAddToChart() {
        this.myChips = this.selectedAgents.filter(i => i.selected);

        // call styleChipsNoData() here once CXOne fixes the bug removing the class after this point

        // used to give each flag series a distinct class - used to add color to the flag later
        let counter = 0;
        for (const agent of this.selectedAgents) {
            // if they already exist in the dataset, skip them
            if (this.chartObject.get(agent.name) || this.chartObject.get(agent.name + "-coachings") ) {
                continue;
            }

            // finding correct idx of agents data array, making and pushing the dataset
            const idx = this.agentsData.findIndex(a => a.name === agent.name);
            if (idx === -1) { // they do not have any data and do not exist in the agentsData array
                continue;
            }

            const scoreDataOptions = this.createAgentScoreLine(agent, idx);
            counter++;
            const coachingsDataOptions = this.createAgentCoachingLine(agent, idx, counter);

            this.chartObject.addSeries(coachingsDataOptions, true);
            this.recolorFlags(agent, counter);

            this.chartObject.addSeries(scoreDataOptions, true); // score line added second so they will be in front of the coachings points
            this.chartObject.reflow();
        };
    }

    private createAgentScoreLine(agent, idx) {
        const dataOptions = {} as any;
        const data = [];
        this.agentsData[idx].avgScore.forEach(avgScoreInfo => {
            const dataPoint = {} as PointObject;
            dataPoint.y = avgScoreInfo.avgScore;
            dataPoint.x = this.chartLabels.indexOf(this.datePipe.transform(avgScoreInfo.date, "MMM d"));
            let numOfEvals = avgScoreInfo.numEvals.toString();
            if (numOfEvals === "1") {
                numOfEvals = ""; // if the num of evals is 1 they dont want a label
            }
            dataPoint.dataLabels = [{
                useHTML: true,
                format: numOfEvals
            }];
            dataPoint.hasTooltip = true;

            data.push(dataPoint);
        });
        dataOptions.data = data;
        dataOptions.color = agent.color;
        dataOptions.id = agent.name;
        dataOptions.name = agent.name;
        dataOptions.yAxis = "yAxis";

        return dataOptions;
    }

    private createAgentCoachingLine(agent, idx, counter) {
        const coachingData = [];
        const coachingsDataOptions = {} as any;
        this.agentsData[idx]?.coachingPoint.forEach(coachingPoint => {
            const dataPoint = {} as PointObject;
            dataPoint.x = coachingPoint.x; // set in fillCoachingInfo()
            dataPoint.y = coachingPoint.y;

            let coachingImageURL = 'url(../App_Themes/CXone/icons/coaching-number.svg#coaching-number)';
            let numCoachings = coachingPoint.numCoachings;
            if (coachingPoint.numCoachings === "1") {
                numCoachings = "";
                coachingImageURL = 'url(../App_Themes/CXone/icons/coaching-center.svg#coaching-center)';
            }
            dataPoint.dataLabels = [{
                useHTML: true,
                format: numCoachings,
                color: agent.color,
                x: 27,
                y: -6
            }];
            dataPoint.marker = {
                symbol: coachingImageURL,
                width: 70,
                height: 70,
            };
            dataPoint.hasTooltip = true;
            dataPoint.className = "FINDME" + counter + this.moduleKey;
            // this is a way to add custom data to your point object that can later be read in formatter function calls, was used to turn off tooltips per point
            // if (this.agentsData[idx].avgScore.find(score => score.date === this.dates[coachingPoint.x].slice(0, 10))) {
            //     dataPoint.hasTooltip = false;
            // }

            coachingData.push(dataPoint);
        });
        coachingsDataOptions.data = coachingData;
        coachingsDataOptions.color = agent.color;
        coachingsDataOptions.id = agent.name + "-coachings";
        coachingsDataOptions.name = agent.name + "-coachings";
        coachingsDataOptions.yAxis = "yAxis";
        coachingsDataOptions.states = {hover: { enabled: false } }; // so line doesnt appear on hover
        coachingsDataOptions.lineWidth = 0;

        return coachingsDataOptions;
    }

    private recolorFlags(agent, counter) {
        // find most recent flags that was added to chart
        const flagList = document.getElementsByClassName("FINDME" + counter + this.moduleKey);
        if (flagList.length > 0) { // if any coaching objects exist color them using agent.color
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < flagList.length; i++) {
                flagList[i].setAttribute("filter", this.colorMap[agent.color]);
            }
        }
    }

    // so it wont try to create the highcharts before the DOM element is available
    ngAfterViewChecked(): void {
        if (this.chartObject === null) {
            this.createChart();
        }
    }

    private async loadData(isAutoRefresh) {
        this.dataReady = false;

        if (this.isDataLoading) {
            return; // don't have multiple outgoing requests
        }


        this.isDataLoading = true;
        const abort = new Promise<void>((resolve, reject) => this.abortLoad = () => resolve());

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        if (this.noAgentsSelected) { //no need to load data when no agents are selected
            const allGroups = (await this.groupSelectorService.loadFilteredGroups(this.moduleKey, BaseGroupType.Employee, 1)).data;
            if (allGroups[1].Groups.length === 0)
                await this.moduleManager.forceLoadSettings(); //force group selector to load since we won't be loading data
            this.moduleManager.showLoader = false;
            this.dataReady = true;
            this.isFirstLoad = false;
            this.isDataLoading = false;
            return;
        }

        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad, abort);

            // this.mockData();

            this.dates = [];
            this.populateDropdown();
            this.organizeData();
            this.createChart();
            this.moduleManager.showLoader = false;
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.moduleManager.setAutoRefreshReady();
            this.isDataLoading = false;
        }
    }

    // back end returns dates in order so these must be later than the latest dates from data pull so dates dont appear out of order
    // (front end expects dates to already be in order) IN ORDER FOR THIS TO WORK YOU MUST ADD DATA OBJECTS TO AGENTS THAT ALREADY EXIST FROM GROUP SELECTOR
    private mockData() {
        this.data.push({
            agent: "RAFAEL PINEDA",
            assignee: "0F9DDA81-6618-47F0-8E03-D384A12174F7",
            assigneeStatus: "COMPLETE",
            assigner: "6C4E52A8-T763-43DA-A34D-145218AE7C9D",
            coachingPackageName: "TEST PACKAGE",
            coachingPlanId: "6C4E52A8-T763-43DA-K5I6-984718AE7C9F",
            coachingPlanName: "",
            dateGroup: "Oct 5",
            dateRecieved: "2022-10-05T05:41:55",
            score: 5,
            rawScore: 5,
            maxPossibleScore: 5,
            segmentContactID: "a",
            segmentID: "ads" as any,
            workflowInstanceID: "asdf",
            workflowStatus: "ffff",
            preferScoreOverRank: true,
            scoringVisible: false,
            changeFormAllowed: true,
            evaluationSubject:"NOT EMP"

        });

        this.data.push({
            agent: "RAFAEL PINEDA",
            assignee: "0F9DDA81-6618-47F0-8E03-D384A12174F7",
            assigneeStatus: "COMPLETE",
            assigner: "6C4E52A8-T763-43DA-A34D-145218AE7C9D",
            coachingPackageName: "TEST PACKAGE",
            coachingPlanId: "6C4E52A8-T763-43DA-K5I6-984718AE7C9F",
            coachingPlanName: "",
            dateGroup: "Oct 5",
            dateRecieved: "2022-10-05T05:41:55",
            score: 27,
            rawScore: 5,
            maxPossibleScore: 5,
            segmentContactID: "a",
            segmentID: "ads" as any,
            workflowInstanceID: "asdf",
            workflowStatus: "dispute",
            preferScoreOverRank: true,
            scoringVisible: false,
            changeFormAllowed: true,
            evaluationSubject: "EMPLOYEE",
        });

        this.data.push({
            agent: "MARVIN RAMIREZ",
            assignee: "0F9DDA81-6618-47F0-8E03-D384A12174F7",
            assigneeStatus: "COMPLETE",
            assigner: "6C4E52A8-T763-43DA-A34D-145218AE7C9D",
            coachingPackageName: "TEST PACKAGE",
            coachingPlanId: "6C4E52A8-T763-43DA-K5I6-984718AE7C9F",
            coachingPlanName: "",
            dateGroup: "Oct 6",
            dateRecieved: "2022-10-06T05:41:55",
            score: 89.55567,
            rawScore: 5,
            maxPossibleScore: 5,
            segmentContactID: "a",
            segmentID: "ads" as any,
            workflowInstanceID: "asdf",
            workflowStatus: "ffff",
            preferScoreOverRank: true,
            scoringVisible: false,
            changeFormAllowed: true,
            evaluationSubject: "EMPLOYEE"
        });
    }

    private populateDropdown() {
        if (this.data?.length > 0) {
            this.agents = [];

            this.colorIndex = 0;
            this.data.forEach(row => {
                this.pushDateIfNew(row.dateRecieved);
                if (this.colorIndex === this.currentTheme.length) {
                    this.colorIndex = 0;
                }
                // if the agent is found in the array skip to next row
                if (this.agents.findIndex(i => i.name === row.agent) !== -1) {
                    return;
                }

                // pulling info to chips
                const agent = {} as AgentDropdown;
                agent.name = row.agent;
                agent.id = this.agents.length;
                if (this.selectedAgents.find(i => i.name === agent.name) && this.selectedAgents.find(i => i.name === agent.name).selected) {
                    agent.selected = true;
                }
                else {
                    agent.selected = false;
                }
                agent.color = this.currentTheme[this.colorIndex];

                this.colorIndex++;
                this.agents.push(agent);
            });

        }

    }

    // used in populateDropdown() to push dates if they are new
    private pushDateIfNew(dateToPush) {
        // ensuring no duplicate dates in dates array
        if (!this.dates.find(d => d.slice(0, 10) === dateToPush.slice(0, 10))) {
            this.dates.push(dateToPush);
        }
    }

    private organizeData() {
        // initialize agentsData object
        this.agentsData = [];
        // as long as we have data
        if (this.data.length > 0) {
            // create and fill new AgentData object from agents in dropdown
            for (const agent of this.agents) {
                const agentData = new AgentData();
                this.organizeDataPerAgent(agentData, agent);
                this.computeAvgs(agentData);
                this.fillCoachingInfo(agentData);
                this.agentsData.push(agentData);
            }
            // this.displayAgentsAndInfo(); // for testing purposes
            this.updateYAxisMinMax();
        }
    }

    private updateYAxisMinMax() {
        const { minAvgScore, maxAvgScore } = this.agentsData.reduce((acc, agent) => {
            agent.avgScore.forEach(score => {
                acc.minAvgScore = Math.min(acc.minAvgScore, score.avgScore);
                acc.maxAvgScore = Math.max(acc.maxAvgScore, score.avgScore);
            });
            return acc;
        }, { minAvgScore: -20, maxAvgScore: 120 });
        const spaceForFlags = 20; // the first and last label wont be shown to accomodate space for flags
        (this.config.yAxis as any).min = Math.floor(minAvgScore) - spaceForFlags;
        (this.config.yAxis as any).max = Math.ceil(maxAvgScore) + spaceForFlags;
    }
    // good for testing purposes to see data after it is crunched
    private displayAgentsAndInfo() {
        console.log("end of all computing avgs " + (this.agentsData.length) + " : ", this.agentsData);
    }

    private organizeDataPerAgent(agentData: AgentData, agent: AgentDropdown) {
        agentData.name = agent.name;

        this.data.forEach((rowData: EvaluationsCoachingData) => {
            if (rowData.agent === agentData.name && rowData.score != null) {
                agentData.scores.push({
                    shortDate: rowData.dateGroup,
                    score: rowData.score,
                    rawScore: rowData.rawScore,
                    maxPossibleScore: rowData.maxPossibleScore,
                    date: rowData.dateRecieved,
                    segmentContactID: rowData.segmentContactID,
                    segmentID: rowData.segmentID,
                    workflowInstanceID: rowData.workflowInstanceID,
                    workflowStatus: rowData.workflowStatus,
                    changeFormAllowed: rowData.changeFormAllowed,
                    scoringVisible: rowData.scoringVisible,
                    preferScoreOverRank: rowData.preferScoreOverRank,
                    evaluationSubject: rowData.evaluationSubject
                } as ScoresObj);
            }
            // this ensures that each agent only gets complete coaching objects that have both a completion data and a coaching name
            if (rowData.agent === agentData.name && rowData.coachingPlanName && rowData.dateRecieved) {
                agentData.coachings.push({
                    coachingName: rowData.coachingPlanName,
                    date: rowData.dateRecieved,
                    shortDate: rowData.dateGroup
                } as CoachingObj);
            }
        });
    }

    private computeAvgs(agent: AgentData) {
        let marks = 0;
        let maxPossibleMarks = 0;
        let countInRange = 0;
        let index = 0;
        for (const scoreObj of agent.scores) {
            const date = scoreObj.date.slice(0, 10);
            if (index === 0) {
                marks += scoreObj.rawScore;
                maxPossibleMarks += scoreObj.maxPossibleScore;
                countInRange++;
                if (agent.scores.length === 1) {
                    agent.avgScore.push({ date, avgScore: marks / maxPossibleMarks * 100, numEvals: countInRange } as AvgScoreInfo);
                }
                index++;
                continue;
            }
            const lastDate = agent.scores[index - 1].date.slice(0, 10);

            // if next score is same date it is in same score grouping
            if (date === lastDate) {
                marks += scoreObj.rawScore;
                maxPossibleMarks += scoreObj.maxPossibleScore;
                countInRange++;
                // if it is the same date and the last element in the array push the avg score
                if (index === agent.scores.length - 1) {
                    agent.avgScore.push({ date, avgScore: marks / maxPossibleMarks * 100, numEvals: countInRange } as AvgScoreInfo);
                }
                index++;
            }
            // different date push the avg score for the current date and reset marks and countInRange
            else {
                agent.avgScore.push({ date: lastDate, avgScore: marks / maxPossibleMarks * 100, numEvals: countInRange } as AvgScoreInfo);
                marks = scoreObj.rawScore;
                maxPossibleMarks = scoreObj.maxPossibleScore;
                countInRange = 1;
                // if last index happens to be a different date push avgScore of new date
                if (index === agent.scores.length - 1) {
                    agent.avgScore.push({ date, avgScore: marks / maxPossibleMarks * 100, numEvals: countInRange } as AvgScoreInfo);
                }
                index++;
            }
        }
    }

    // used in fill coaching info
    private getAvg(agent: AgentData, coachingDay) {
        for (const day of agent.avgScore as AvgScoreInfo[]) {
            if (day.date === coachingDay) {
                return day.avgScore;
            }
        }
    }

    // used in fill coaching info
    private pushCoachingPoint(agent, avgScore, numCoachings, date) {
        agent.coachingPoint.push({
            x: this.dates.findIndex(e => e.slice(0, 10) === date),
            y: avgScore,
            numCoachings: numCoachings.toString()
        } as CoachingPoint);
    }

    private fillCoachingInfo(agent: AgentData) {
        let numCoachings = 0;
        let counter = 0;
        let avgScore = -1;
        for (const coachingObj of agent.coachings as CoachingObj[]) {
            let date;
            if (counter === 0) {
                numCoachings++;
                if (agent.coachings.length === 1) {
                    date = coachingObj.date.slice(0, 10);
                    avgScore = this.getAvg(agent, date);
                    if (!avgScore) { // if no evals were taken this day
                        avgScore = this.getAvgIfNull(agent, date);
                    }
                    this.pushCoachingPoint(agent, avgScore, numCoachings, date);
                }
                counter++;
                continue;
            }
            date = coachingObj.date.slice(0, 10);
            const lastDate = agent.coachings[counter - 1].date.slice(0, 10);

            // if next coaching is same date it is in same coaching day grouping
            if (date === lastDate) {
                numCoachings++;
                // if it is the last element in the array push the point
                if (counter === agent.coachings.length - 1) {
                    avgScore = this.getAvg(agent, date);
                    if (!avgScore) {
                        avgScore = this.getAvgIfNull(agent, date);
                    }
                    this.pushCoachingPoint(agent, avgScore, numCoachings, date);
                }
                counter++;
            }
            // different date push the coaching point for the current date and reset numCoaching
            else {
                avgScore = this.getAvg(agent, lastDate);
                if (!avgScore) {
                    avgScore = this.getAvgIfNull(agent, lastDate);
                }
                this.pushCoachingPoint(agent, avgScore, numCoachings, lastDate);
                numCoachings = 1;
                // if last index happens to be a different date push coaching of new date
                if (counter === agent.coachings.length - 1) {
                    avgScore = this.getAvg(agent, date); // need to do this again it is overridden above on an edge case
                    if (!avgScore) {
                        avgScore = this.getAvgIfNull(agent, date);
                    }
                    this.pushCoachingPoint(agent, avgScore, numCoachings, date);
                }
                counter++;
            }
        };
    }

    // interpolate between non null eval points for coaching point
    private getAvgIfNull(agent: AgentData, dateOfNull) {
        let avg = 0;
        const idxOfDate = this.dates.findIndex(date => dateOfNull === date.slice(0,10)); // we know current is null for scores
        const rightPointInfo = this.getRightAvg(agent, idxOfDate + 1);
        const leftPointInfo = this.getLeftAvg(agent, idxOfDate - 1);
        avg = this.newAvg(rightPointInfo, leftPointInfo, idxOfDate);
        return avg;
    }

    // find eval point to the right of coaching point
    private getRightAvg(agent: AgentData, idxOfDate): NearestPointInfo {
        const nearestPointInfo: NearestPointInfo = {nearestPointAvg: -1, idxOfNonNull: -1};
        let pointsAway = 1;
        const initialDate = idxOfDate;
        for (idxOfDate; idxOfDate < this.dates.length; idxOfDate++) {
            const evalPoint = agent.avgScore.findIndex(pointInfo => pointInfo.date === this.dates[idxOfDate].slice(0,10));
            if (evalPoint === -1) {
                pointsAway++;
            }
            else {
                nearestPointInfo.nearestPointAvg = agent.avgScore[evalPoint].avgScore;
                nearestPointInfo.idxOfNonNull = initialDate - 1 + pointsAway;
                break;
            }
        }
        return nearestPointInfo;
    }

    // find eval point to the left of coaching point
    private getLeftAvg(agent: AgentData, idxOfDate): NearestPointInfo {
        const nearestPointInfo: NearestPointInfo = {nearestPointAvg: -1, idxOfNonNull: -1};
        let pointsAway = 1;
        const initialDate = idxOfDate;
        for (idxOfDate; idxOfDate >= 0; idxOfDate--) {
            const evalPoint = agent.avgScore.findIndex(pointInfo => pointInfo.date === this.dates[idxOfDate].slice(0,10));
            if (evalPoint === -1) {
                pointsAway++;
            }
            else {
                nearestPointInfo.nearestPointAvg = agent.avgScore[evalPoint].avgScore;
                nearestPointInfo.idxOfNonNull = initialDate + 1 - pointsAway;
                break;
            }
        }
        return nearestPointInfo;
    }

    /**
     * interpolated average for coaching point based upon eval point to the left and right of coaching point
     * if no point to left coaching point will align with right eval point
     * if no point to the right coaching point will align with left eval point
     * if no point to left or right coaching point will be on x axis
     */
    private newAvg(rightInfo: NearestPointInfo, leftInfo: NearestPointInfo, idxOfDate): number {
        let interpolatedAvg = 0;
        if (rightInfo.idxOfNonNull === -1 && leftInfo.idxOfNonNull !== -1) {
            interpolatedAvg = leftInfo.nearestPointAvg;
        }
        if (rightInfo.idxOfNonNull !== -1 && leftInfo.idxOfNonNull === -1) {
            interpolatedAvg = rightInfo.nearestPointAvg;
        }
        if (rightInfo.idxOfNonNull === -1 && leftInfo.idxOfNonNull === -1) {
            interpolatedAvg = 0;
        }
        if (rightInfo.idxOfNonNull !== -1 && leftInfo.idxOfNonNull !== -1) {
            const riseOverRun = (rightInfo.nearestPointAvg - leftInfo.nearestPointAvg) / (rightInfo.idxOfNonNull - leftInfo.idxOfNonNull);
            interpolatedAvg = leftInfo.nearestPointAvg + (riseOverRun * (idxOfDate - leftInfo.idxOfNonNull));
        }

        return interpolatedAvg;
    }

    // last configurations for chart and adding dates to chart so it isnt a bare chart
    private createChart() {

        // dates array is done being modified at this point so these two arrays may be initialized
        this.chartLabels = this.dates.map(e => this.datePipe.transform(e, "MMM d"));
        this.fullDates = this.dates.map(e => this.datePipe.transform(e, "MMM dd, yyyy"));
        const readyLabels = [];
        this.chartLabels.forEach(element => {
            readyLabels.push([element, null]);
        });

        const that = this;
        // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
        this.config.tooltip.formatter = function () {
            // below is to turn tooltips off on various points
            // if (!(this.series.chart.series[this.series.index] as any).data[this.point.index].hasTooltip) return false;
            let dayHasEval;
            let dayHascoaching;
            const chartHeight = this.series.chart.chartHeight - 30;
            if (this.series.name.includes("coachings")) {
                dayHasEval = that.getEntryOnDay(that.chartObject.get(this.series.name.slice(0, this.series.name.indexOf('-'))), this.key);
                dayHascoaching = that.getEntryOnDay(that.chartObject.get(this.series.name), this.key);
            }
            else {
                dayHasEval = that.getEntryOnDay(that.chartObject.get(this.series.name), this.key);
                dayHascoaching = that.getEntryOnDay(that.chartObject.get(this.series.name + "-coachings"), this.key);
            }

            // this will resolve cases when there is a coaching and evals on a day or just an eval
            if (dayHasEval) {
                // style='z-index: 999999!important; position: absolute' tooltip issue FIX
                let toReturn = "<div id='tooltip' style='max-height: "+ chartHeight +";'>" +
                    "<div class='colorBox' style='display: inline-block; background-color: " + this.color +
                    "; width: 9px; height: 9px;'></div>" + //color box
                    "<span class='name' style='font-size: 13px;'>  " + that.trimIfCoaching(this.series.name) + "</span>" + // name
                    "<div style='min-width: 55px; min-height: 2px; display: inline-block'></div>"; //space seperating name and date
                toReturn += "<span class='dateTooltip' style='color: #8C95A5;'>" + that.fullDates[this.point.options.x] + "</span>"; // date
                let paddingForAvgScore = "style='padding-top: 7px;'";
                let both = false;
                if (dayHascoaching) {
                    both = true;
                    toReturn += that.getCoachings(this.key, this.series.name, both, chartHeight); // coachings
                    paddingForAvgScore = ''; // fix padding if coachings exist
                    toReturn += "<hr style='width:100%;text-align:left;margin-left:0;height:2px;border:none;background-color: lightgray;'>"; // break line
                }
                toReturn += "<div " + paddingForAvgScore + ">" +
                    "<div style='display: inline-block; padding-right: 4px; font-size: 13px'>Average Score: </div>" +
                    "<div style='display: inline-block; font-weight: bold; font-size: 13px'>" + this.y.toFixed(2) + "</div>" +
                    "</div>"; //avg score
                if (!dayHascoaching) {
                    toReturn += "<hr style='width:100%;text-align:left;margin-left:0;height:2px;border:none;background-color: lightgray;'>"; // break line
                }
                toReturn += that.getScores(this.key, this.series.name, both, chartHeight); // scores
                toReturn += "</div>"; // ending div matches with first line id-tooltip
                return toReturn;
            }
            else { // else it must only be coachings
                let toReturn = "<div id='tooltip'>" +
                    "<div class='colorBox' style='display: inline-block; background-color: " + this.color +
                    "; width: 9px; height: 9px;'></div>" + //color box
                    "<span class='name' style='font-size: 13px;'>  " + this.series.name.slice(0, this.series.name.indexOf('-')) + "</span>"+ // name
                    "<div style='min-width: 55px; min-height: 2px; display: inline-block'></div>"; //space seperating name and date
                toReturn += "<span class='dateTooltip' style='color: #8C95A5;'>" + that.fullDates[this.point.options.x] + "</span>"; // date
                toReturn += that.getCoachings(this.key, this.series.name, false, chartHeight); // coachings
                toReturn += "</div>";
                return toReturn;
            }
        };

        // create chart (chart object set via angular in front end)

        this.emptyChart();

        (this.config.xAxis as any).categories = this.chartLabels;
        this.chartObject.addSeries({ data: readyLabels, id: "dates", name: "dates", yAxis: "yAxis" }, true); // makes the x axis show labels with no data
        requestAnimationFrame(() => this.chartObject?.reflow());
        this.updateChart = true;
        this.dataReady = true;
    }

    public emptyChart() {
        while(this.chartObject.series.length > 0)
            this.chartObject.series[0].remove(true);
        this.myChips = [];
    }

    //used to trim name if it is a coaching point
    private trimIfCoaching(name) {
        if (name.includes("coachings")) {
            return name.slice(0, name.indexOf("-"));
        }
        else {
            return name;
        }
    }

    // used to know if there were coachings or evals on a given day for tooltip logic
    private getEntryOnDay(line: Highcharts.Series, dayToMatch: string) {
        let coachingOrEvalOnDay = false;
        line?.data.forEach(point => {
            if (point.category === dayToMatch) {
                coachingOrEvalOnDay = true;
            }
        });
        return coachingOrEvalOnDay;
    }

    // used in tooltip
    private getScores(pointxLabel: string, name: string, both: boolean, chartHeight: number) {
        name = this.trimIfCoaching(name);
        let evaluationCount = 1;
        let maxHeight = chartHeight - 60;
        if (both) {
            maxHeight = (chartHeight - 60) / 2;
        }
        const windowOptions = "width=650,height=650";
        let all = "<div style='overflow-y: auto; max-height:" + maxHeight + "px;'>"; // adding scroll here
        this.agentsData[this.getIdx(name)].scores.forEach(scoreObj => {
            if (pointxLabel === scoreObj.shortDate) {
                if (scoreObj.workflowStatus.toLowerCase() === "dispute" || scoreObj.workflowStatus.toLowerCase() === "in review") {
                    const toReturn =
                    `<div>
                        <span>Score ${evaluationCount}: </span>
                        <a href="#" 
                            onclick="window.open('${this.getScorePlayerURL(scoreObj)}','', '${windowOptions},top=0,left=0');" 
                            style="color: #0078AC;">${scoreObj.score.toFixed(2)}
                        </a>
                    </div>`;
                    all += toReturn;
                    evaluationCount++;
                }
                else if (scoreObj.evaluationSubject?.toLowerCase() === "employee") { // in this case only open formExecutor window
                    const toReturn =
                    `<div>
                        <span>Score ${evaluationCount}: </span>
                        <a href="#" 
                            onclick="window.open('${this.getScoreFormURL(scoreObj)}','', '${windowOptions},top=0,left=0')" 
                            style="color: #0078AC;">${scoreObj.score.toFixed(2)}
                        </a>
                    </div>`;
                    all += toReturn;
                    evaluationCount++;
                }
                else { // all other cases open both windows
                    const toReturn =
                        `<div>
                            <span>Score ${evaluationCount}: </span>
                            <a href="#" 
                                onclick="window.open('${this.getScorePlayerURL(scoreObj)}','', '${windowOptions},top=0,left=0');
                                    window.open('${this.getScoreFormURL(scoreObj)}','', '${windowOptions},top=0,left=655')" 
                                style="color: #0078AC;">${scoreObj.score.toFixed(2)}
                            </a>
                        </div>`;
                    all += toReturn;
                    evaluationCount++;
                }
            }
        });
        all += "</div>";
        return all;
    }

    // used to get url of clickable score in tooltip
    private getScorePlayerURL(scoreObj: ScoresObj) {
        const cxoneDomain = this.navigationService.cxoneTokenUrls;
        let cxoneScoreUrl = cxoneDomain?.niceBaseUrl;
        cxoneScoreUrl += `/player/#/cxone-player/contacts/${scoreObj.segmentContactID}/segments/${scoreObj.segmentID}?mediaType=all`;
        return cxoneScoreUrl;
    }
    // used to get url of clickable score in tooltip DO I NEED TO MAKE THIS NOT POP IF WORKFLOWINSTANCEID == null
    private getScoreFormURL(scoreObj: ScoresObj) {
        const cxoneDomain = this.navigationService.cxoneTokenUrls;
        let cxoneScoreUrl = cxoneDomain?.niceBaseUrl;
        cxoneScoreUrl += `/qm/#/formExecutor?workFlowInstanceId=` +
            `${btoa(scoreObj.workflowInstanceID.toLowerCase())}&changeFormAllowed=` +
            `${btoa(scoreObj.changeFormAllowed.toString())}&scoringVisible=` +
            `${btoa(scoreObj.scoringVisible.toString())}&preferScoreOverRank=` +
            `${btoa(scoreObj.preferScoreOverRank.toString())}`;
        return cxoneScoreUrl;
    }

    // used in tooltip
    private getCoachings(pointxLabel: string, name: string, both: boolean, chartHeight: number) {
        name = this.trimIfCoaching(name);
        let maxHeight = chartHeight - 60;
        if (both) {
            maxHeight = (chartHeight - 60) / 2;
        }
        let all = "<div style='overflow-y: auto; max-height: " + maxHeight + "px;'>"; // adding scroll here
        let coachingNum = 1;
        let counter = 0;
        this.agentsData[this.getIdx(name)].coachings.forEach(coachingObj => {
            if (pointxLabel === coachingObj.shortDate) {
                let toReturn = '';
                if (counter === 0) {
                    toReturn = "<div style='padding-top: 7px;'><span>Coaching " + coachingNum + ": </span>" +
                        "<span style='font-weight: bold;'>" + coachingObj.coachingName + "</span></div>";
                }
                else {
                    toReturn = "<div><span>Coaching " + coachingNum + ": </span>" +
                        "<span style='font-weight: bold;'>" + coachingObj.coachingName + "</span></div>";
                }
                all += toReturn;
                coachingNum++;
                counter++;
            }
        });
        all += "</div>";
        return all;
    }

}

